import React, { useState, useEffect } from 'react';
import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
} from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Table } from 'reactstrap';

export default function TestDragAndDrop({ items, columns, onGroupMoved, onItemMoved, onGroupExpandedChanged, onRowClick }) {
    const [proposedDepth, setProposedDepth] = useState(0);
    const [initialDepth, setInitialDepth] = useState(0);
    const [overId, setOverId] = useState(null);
    const [isInvalidDrop, setIsInvalidDrop] = useState(false);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    function isChildOf(child, parent) {
        if (!child || !parent) return false;
        // In your data model, depth=1 means direct child of nearest preceding depth=0
        // So check if 'parent' precedes 'child' and there is no other depth=0 in between

        const parentIndex = items.findIndex(i => i.id === parent.id);
        const childIndex = items.findIndex(i => i.id === child.id);
        if (parentIndex === -1 || childIndex === -1) return false;
        if (childIndex <= parentIndex) return false;

        for (let i = parentIndex + 1; i < childIndex; i++) {
            if (items[i].depth === 0) {
                // There's another group in between, so 'child' is not child of 'parent'
                return false;
            }
        }
        return true;
    }

    const handleDragStart = (event) => {
        const item = items.find(i => i.id === event.active.id);
        if (item) {
            setInitialDepth(item.depth);
        }
    };

    const handleDragMove = (event) => {
        // Can't change depth
        setProposedDepth(initialDepth);
    };

    const handleDragOver = (event) => {
        const overId = event.over?.id || null;
        setOverId(overId);

        if (!overId) {
            setIsInvalidDrop(false);
            return;
        }

        const activeItem = items.find(i => i.id === event.active.id);
        if (!activeItem) {
            setIsInvalidDrop(false);
            return;
        }

        // Find indices in visibleItems
        const visibleActiveIndex = visibleItems.findIndex(i => i.id === event.active.id);
        const visibleOverIndex = visibleItems.findIndex(i => i.id === overId);

        if (visibleActiveIndex === -1 || visibleOverIndex === -1) {
            setIsInvalidDrop(false);
            return;
        }

        // Use visible subtree for index calculations
        const visibleSubtree = activeItem.depth === 0
            ? getSubtree(visibleItems, visibleActiveIndex)
            : [activeItem];

        const remainingVisible = visibleItems.filter(i => !visibleSubtree.includes(i));

        // Calculate insert position using visible subtree length
        const insertAt = visibleOverIndex < visibleActiveIndex
            ? visibleOverIndex
            : visibleOverIndex - (visibleSubtree.length - 1);

        if (activeItem.depth === 0) {
            const prev = insertAt > 0 ? remainingVisible[insertAt - 1] : null;
            const next = insertAt < remainingVisible.length ? remainingVisible[insertAt] : null;

            const prevIsChild = prev?.depth === 1;
            const nextIsChild = next?.depth === 1;

            // Prevent placing a group between two children (siblings)
            if (prevIsChild && nextIsChild) {
                setIsInvalidDrop(true);
                return;
            }

            // Prevent placing a group between a depth=0 and its own child (causes reassignment)
            if (
                (prev?.depth === 0 && nextIsChild && isChildOf(next, prev)) ||
                (next?.depth === 0 && prevIsChild && isChildOf(prev, next))
            ) {
                setIsInvalidDrop(true);
                return;
            }
        }

        setIsInvalidDrop(false);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!active || !over) return cleanup();

        console.log("onDragEnd")

        // Find visible indices
        const visibleActiveIndex = visibleItems.findIndex(i => i.id === active.id);
        const visibleOverIndex = visibleItems.findIndex(i => i.id === over.id);

        if (visibleActiveIndex === -1 || visibleOverIndex === -1) {
            cleanup();
            return;
        }

        // Find full item index for active item
        const activeIndexFull = items.findIndex(i => i.id === active.id);
        const activeItem = items[activeIndexFull];

        // Get visible subtree (for insert calculations)
        const visibleSubtree = activeItem.depth === 0
            ? getSubtree(visibleItems, visibleActiveIndex)
            : [activeItem];

        // Get full subtree (for data reorder)
        const fullSubtree = activeItem.depth === 0
            ? getSubtree(items, activeIndexFull)
            : [activeItem];

        // Remaining visible items excluding visible subtree
        const remainingVisible = visibleItems.filter(i => !visibleSubtree.includes(i));

        // Calculate insertion index in visibleItems
        const insertAt = visibleOverIndex < visibleActiveIndex
            ? visibleOverIndex
            : visibleOverIndex - (visibleSubtree.length - 1);

        // Don't allow children to be dropped to the very top of the list, as that's
        // would make it not be in under a group
        if (activeItem.depth === 1 && insertAt === 0) {
            cleanup();
            return;
        }

        if (activeItem.depth === 0) {
            const prev = insertAt > 0 ? remainingVisible[insertAt - 1] : null;
            const next = insertAt < remainingVisible.length ? remainingVisible[insertAt] : null;

            const prevIsChild = prev?.depth === 1;
            const nextIsChild = next?.depth === 1;

            if (prevIsChild && nextIsChild) {
                cleanup();
                return;
            }

            if (
                (prev?.depth === 0 && nextIsChild && isChildOf(next, prev)) ||
                (next?.depth === 0 && prevIsChild && isChildOf(prev, next))
            ) {
                cleanup();
                return;
            }
        }

        // Remove full subtree from full items
        const subtreeIds = new Set(fullSubtree.map(i => i.id));
        const remainingFullItems = items.filter(i => !subtreeIds.has(i.id));

        // Find the ID of the item before the insert position in visible list
        const prevVisibleItem = insertAt > 0 ? remainingVisible[insertAt - 1] : null;

        let insertionIndexInFull;

        if (!prevVisibleItem) {
            // Insert at start
            insertionIndexInFull = 0;
        } else {
            // Find index of prevVisibleItem in remainingFullItems
            const prevIndexInFull = remainingFullItems.findIndex(i => i.id === prevVisibleItem.id);
            if (prevIndexInFull === -1) {
                // If not found, append at the end
                insertionIndexInFull = remainingFullItems.length;
            } else {
                if (prevVisibleItem.depth === 0 && !prevVisibleItem.expanded) {
                    // Insert after the entire subtree of prevVisibleItem if it's collapsed
                    const prevSubtree = getSubtree(remainingFullItems, prevIndexInFull);
                    insertionIndexInFull = prevIndexInFull + prevSubtree.length;
                } else {
                    // Insert right after the prevVisibleItem (depth=1)
                    insertionIndexInFull = prevIndexInFull + 1;
                }
            }
        }

        // Insert full subtree at insertionIndexInFull
        const newItems = [
            ...remainingFullItems.slice(0, insertionIndexInFull),
            ...fullSubtree,
            ...remainingFullItems.slice(insertionIndexInFull),
        ];

        if (activeItem.depth === 0) {
            if (activeItem.depth === 0) {
                const flatGroups = newItems.filter(i => i.depth === 0);
                const newPosition = flatGroups.findIndex(i => i.id === activeItem.id);
                onGroupMoved?.(active.id, newPosition);
            }
        } else {
            const findParentGroupId = (itemList, childId) => {
                const index = itemList.findIndex(i => i.id === childId);
                if (index === -1) return null;
                for (let i = index - 1; i >= 0; i--) {
                    if (itemList[i].depth === 0) return itemList[i].id;
                }
                return null;
            };

            const fromGroupId = findParentGroupId(items, active.id);
            const toGroupId = findParentGroupId(newItems, active.id);

            const positionInGroup = (() => {
                let index = newItems.findIndex(i => i.id === active.id);
                if (index === -1) return null;
                let pos = 0;
                for (let i = index - 1; i >= 0; i--) {
                    if (newItems[i].depth === 0) break;
                    if (newItems[i].depth === 1) pos++;
                }
                return pos;
            })();

            onItemMoved?.({
                itemId: active.id,
                fromGroupId,
                toGroupId,
                newPosition: positionInGroup
            });
        }

        //setItems(newItems);
        cleanup();
    };


    const cleanup = () => {
        setProposedDepth(0);
        setInitialDepth(0);
        setOverId(null);
    };

    const toggleExpand = (id) => {
        var editedItem = items.find(x => x.id === id)

        onGroupExpandedChanged?.(id, !editedItem.expanded)
    };

    const visibleItems = getVisibleItems(items);

    return (
        <>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragMove={handleDragMove}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
            >
                <SortableContext items={visibleItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    <Table hover striped responsive>
                        <thead>
                            <tr>{columns.map(col => <th key={col.key}>{col.label}</th>)}</tr>
                        </thead>
                        <tbody>
                            {visibleItems.map((item, index) => {
                                const showDrop = visibleItems[index + 1]?.id === overId;
                                let opacity = 1

                                if (item.rowType === "group") {
                                    if (!item.visible) {
                                        opacity = 0.2
                                    } else if (item.showChildren) {
                                        opacity = 1
                                    } else {
                                        opacity = 0.75 // Same opacity as a normal item
                                    }
                                } else if (item.rowType === "child") {
                                    const parentGroup = getParent(items, item)

                                    if (!parentGroup.visible || !parentGroup.showChildren || !item.visible) {
                                        // Child won't be shown on the quote, so make it almost-transparent on screen
                                        opacity = 0.2
                                    } else {
                                        // Item is a child and it's to be shown on quote.  Show it a slightly less opaque than it's parent on screen
                                        opacity = 0.75
                                    }
                                }

                                return (
                                    <React.Fragment key={item.id}>
                                        <TreeItem
                                            {...item}
                                            hasChildren={hasChildren(items, item)}
                                            onToggle={() => toggleExpand(item.id)}
                                            columns={columns}
                                            item={item}
                                            showDrop={showDrop}
                                            proposedDepth={proposedDepth}
                                            onRowClick={onRowClick}
                                            opacity={opacity}
                                        />
                                        {showDrop && <DropIndicator depth={proposedDepth} isInvalid={isInvalidDrop} />}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </Table>
                </SortableContext>
            </DndContext>
        </>
    );
}

function getVisibleItems(items) {
    const visible = [];
    let skipDepth = Infinity;
    for (const item of items) {
        if (item.depth > skipDepth) continue;
        visible.push(item);
        skipDepth = item.expanded ? Infinity : item.depth;
    }
    return visible;
}

function getSubtree(items, startIndex) {
    const startDepth = items[startIndex].depth;
    const subtree = [items[startIndex]];
    for (let i = startIndex + 1; i < items.length; i++) {
        if (items[i].depth <= startDepth) break;
        subtree.push(items[i]);
    }
    return subtree;
}

function getParent(allItems, child) {
    const startIdx = allItems.findIndex(i => i.id === child.id);
    for (let i = startIdx - 1; i >= 0; i--) {
        if (allItems[i].depth < child.depth) return allItems[i];
    }

    return undefined;
}

function hasChildren(allItems, parent) {
    const startIdx = allItems.findIndex(i => i.id === parent.id);
    for (let i = startIdx + 1; i < allItems.length; i++) {
        if (allItems[i].depth <= parent.depth) break;
        if (allItems[i].depth === parent.depth + 1) return true;
    }
    return false;
}

function TreeItem({ id, depth, expanded, hasChildren, onToggle, columns, item, showDrop, proposedDepth, opacity, onRowClick }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition
    } = useSortable({ id });

    return (
        <tr
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition, opacity: opacity }}
            {...attributes}
            {...listeners}
        >
            {columns.map((col, ci) => (
                <td key={col.key} className={col.className}>
                    <div
                        className={"d-flex align-items-center".concat(col.align && col.align === "right" ? ' justify-content-end' : "")}
                        style={{ paddingLeft: ci === 0 ? `${depth * 1.5}rem` : 0 }}
                    >
                        {ci === 0 && (
                            <span className="me-2 text-muted">
                                <i className="bi bi-grip-vertical" />
                            </span>
                        )}

                        {ci === 0 && hasChildren && (
                            <>

                                <i
                                    className={`bi ${expanded ? 'bi-chevron-down' : 'bi-chevron-right'
                                        } me-2`}
                                    onClick={onToggle}
                                    style={{ cursor: 'pointer' }}
                                />
                            </>
                        )}
                        <div
                            onClick={() => {
                                onRowClick(item);
                            }}
                        >
                            {col.render ? col.render(item[col.key], item) : item[col.key]}
                        </div>
                    </div>
                </td>
            ))}
        </tr>
    );
}

function DropIndicator({ depth, isInvalid }) {
    return (
        <tr style={{ height: 0 }}>
            <td colSpan={6} style={{ padding: 0, border: 'none' }}>
                <div
                    style={{
                        height: '2px',
                        backgroundColor: isInvalid ? 'red' : 'lightgreen',
                        marginLeft: `${depth * 1.5}rem`,
                        transition: 'margin-left 0.2s ease, background-color 0.2s ease',
                    }}
                />
            </td>
        </tr>
    );
}
